import {
  ChevronDownIcon,
  ChevronUpIcon,
  RocketLaunchIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';
import { useState } from 'react';
import { formatAppName } from '../lib/appName';
import type { Deployment, DeploymentQueueItem, InfrastructureApp } from '../types';
import { CopyableVersionTag } from './CopyableVersionTag';
import { Button } from './ui/button';

const badgeClassName = 'inline-flex items-center rounded px-2 py-0.5 text-xs font-medium leading-4';

interface DeploymentQueueTrayProps {
  items: DeploymentQueueItem[];
  apps: InfrastructureApp[];
  deployments: Deployment[];
  bulkActions?: Array<{
    label: string;
    count: number;
    onClick: () => void;
  }>;
  onItemClick?: (appName: string) => void;
  onRemove: (appName: string, environment: string) => void;
  onClear: () => void;
  onContinue: () => void;
}

export const DeploymentQueueTray = ({
  items,
  apps,
  deployments,
  bulkActions = [],
  onItemClick,
  onRemove,
  onClear,
  onContinue,
}: DeploymentQueueTrayProps) => {
  const [collapsed, setCollapsed] = useState(false);
  const hasBulkActions = bulkActions.some((action) => action.count > 0);
  const hasQueuedItems = items.length > 0;

  if (!hasQueuedItems && !hasBulkActions) {
    return null;
  }

  const prodCount = items.filter((item) => item.environment === 'prod').length;
  const devCount = items.filter((item) => item.environment === 'dev').length;
  const isSensitiveDeployment = (item: DeploymentQueueItem) =>
    apps
      .find((app) => app.name === item.appName)
      ?.bulkDeploymentExclusions.includes(item.environment) ?? false;
  const getLatestDeployment = (item: DeploymentQueueItem) =>
    deployments
      .filter(
        (deployment) =>
          deployment.appName === item.appName && deployment.environment === item.environment,
      )
      .sort(
        (left, right) => new Date(right.createdAt).getTime() - new Date(left.createdAt).getTime(),
      )[0];
  const sortedItems = [...items].sort((left, right) => {
    const appNameComparison = formatAppName(left.appName).localeCompare(
      formatAppName(right.appName),
    );

    if (appNameComparison !== 0) {
      return appNameComparison;
    }

    return left.environment.localeCompare(right.environment);
  });

  return (
    <div className='fixed bottom-5 right-5 z-40 w-[360px] rounded-lg border border-gray-200 bg-white shadow-xl'>
      <div className='flex items-start justify-between gap-3 px-4 py-3'>
        <div className='min-w-0'>
          <div className='flex items-center gap-2 text-sm font-semibold text-gray-900'>
            <RocketLaunchIcon className='h-4 w-4 text-primary' />
            <span>Deployment queue</span>
          </div>
          <div className='mt-1 text-xs text-gray-500'>
            {items.length} target{items.length === 1 ? '' : 's'}
            {devCount > 0 && ` · ${devCount} dev`}
            {prodCount > 0 && ` · ${prodCount} prod`}
          </div>
        </div>
        {hasQueuedItems && (
          <button
            type='button'
            onClick={() => setCollapsed((current) => !current)}
            className='rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700'
            aria-label={collapsed ? 'Expand deployment queue' : 'Collapse deployment queue'}
            title={collapsed ? 'Expand queue' : 'Collapse queue'}
          >
            {collapsed ? (
              <ChevronUpIcon className='h-4 w-4' />
            ) : (
              <ChevronDownIcon className='h-4 w-4' />
            )}
          </button>
        )}
      </div>

      <div
        className={`grid transition-[grid-template-rows] duration-200 ease-out ${
          hasQueuedItems && collapsed ? 'grid-rows-[0fr]' : 'grid-rows-[1fr]'
        }`}
      >
        <div className='min-h-0 overflow-hidden'>
          <div className='border-t border-gray-100' />
          {hasQueuedItems ? (
            <div className='max-h-72 overflow-y-auto p-2'>
              {sortedItems.map((item) => {
                const sensitive = isSensitiveDeployment(item);
                const latestDeployment = getLatestDeployment(item);
                const envColorClass =
                  item.environment === 'prod'
                    ? 'bg-green-100 text-green-800'
                    : 'bg-blue-100 text-blue-800';

                return (
                  <div
                    key={`${item.appName}:${item.environment}`}
                    role={onItemClick ? 'button' : undefined}
                    tabIndex={onItemClick ? 0 : undefined}
                    onClick={(event) => {
                      if ((event.target as HTMLElement).closest('button')) {
                        return;
                      }

                      onItemClick?.(item.appName);
                    }}
                    onKeyDown={(event) => {
                      if (!onItemClick || (event.key !== 'Enter' && event.key !== ' ')) {
                        return;
                      }

                      event.preventDefault();
                      onItemClick(item.appName);
                    }}
                    className={`flex items-center justify-between gap-3 rounded-md px-2 py-2 hover:bg-gray-50 ${
                      onItemClick
                        ? 'cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring'
                        : ''
                    }`}
                    title={onItemClick ? `Scroll to ${formatAppName(item.appName)}` : undefined}
                  >
                    <div className='min-w-0'>
                      <div className='truncate text-sm font-medium text-gray-900'>
                        {formatAppName(item.appName)}
                      </div>
                      <div className='mt-1 flex items-center gap-1.5'>
                        <span className={`${badgeClassName} ${envColorClass}`}>
                          {item.environment}
                        </span>
                        {sensitive && (
                          <span
                            className={`${badgeClassName} bg-secondary text-secondary-foreground`}
                            title='Excluded from bulk deployment actions'
                          >
                            Sensitive
                          </span>
                        )}
                        {latestDeployment ? (
                          <CopyableVersionTag version={latestDeployment.version} />
                        ) : (
                          <span className='text-xs text-gray-500'>new</span>
                        )}
                      </div>
                    </div>
                    <button
                      type='button'
                      onClick={(event) => {
                        event.stopPropagation();
                        onRemove(item.appName, item.environment);
                      }}
                      className='rounded p-1 text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-700'
                      aria-label={`Remove ${item.appName} ${item.environment} from queue`}
                      title='Remove'
                    >
                      <XMarkIcon className='h-4 w-4' />
                    </button>
                  </div>
                );
              })}
            </div>
          ) : (
            <div className='space-y-1.5 p-4 pt-3'>
              <div className='text-xs font-medium text-gray-500'>Queue outdated apps</div>
              <div className='grid grid-cols-2 gap-2'>
                {bulkActions.map((action) => (
                  <Button
                    key={action.label}
                    variant='outline'
                    size='sm'
                    onClick={action.onClick}
                    disabled={action.count === 0}
                  >
                    {action.label} ({action.count})
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className='border-t border-gray-100 px-4 py-3'>
        <div className='flex items-center justify-end gap-2'>
          {hasQueuedItems && (
            <Button variant='outline' size='sm' onClick={onClear}>
              Clear
            </Button>
          )}
          <Button size='sm' disabled={!hasQueuedItems} onClick={onContinue}>
            Continue
          </Button>
        </div>
      </div>
    </div>
  );
};
